import * as React from "react";
import { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { FormControl, Grid, InputLabel, Select, FormControlLabel, Checkbox, Typography } from "@mui/material";

const formatNumberString = (numberString) => {
	const number = Number(numberString);
	if (isNaN(number)) {
		return "Invalid number";
	}
	return number.toLocaleString();
};

export default function UnitMetaDataEditForm({
	unitRent,
	handleChangeUnitRent,
	rentCycle,
	handleChangeRentCycle,
	changeReason,
	handleChangeReason,
	effectiveStartDate,
	handleChangeEffectiveStartDate,
	allowedEffectiveDates = { has_restriction: false, allowed_range: "Any date" },
	useDeclaredRentForDisplay,
	handleChangeUseDeclaredRent,
	declaredUnitRent,
	handleChangeDeclaredUnitRent,
	declaredRentError,
}) {
	const [dateError, setDateError] = useState("");

	const declaredRentRatio =
		useDeclaredRentForDisplay && unitRent && declaredUnitRent && Number(unitRent) > 0
			? Math.round((Number(declaredUnitRent) / Number(unitRent)) * 100)
			: null;

	useEffect(() => {
		if (!effectiveStartDate) {
			setDateError("");
			return;
		}

		if (allowedEffectiveDates?.has_restriction && allowedEffectiveDates?.payment_day) {
			const date = new Date(effectiveStartDate);
			const dayOfMonth = date.getDate();
			
			if (dayOfMonth !== allowedEffectiveDates.payment_day) {
				setDateError(`Only the ${allowedEffectiveDates.payment_day}th of any month is allowed`);
			} else {
				setDateError("");
			}
		} else {
			setDateError("");
		}
	}, [effectiveStartDate, allowedEffectiveDates]);

	const validateAndHandleDateChange = (event) => {
		const selectedDate = event.target.value;
		handleChangeEffectiveStartDate(event);
		
		if (!selectedDate) {
			setDateError("");
			return;
		}

		if (allowedEffectiveDates?.has_restriction && allowedEffectiveDates?.payment_day) {
			const date = new Date(selectedDate);
			const dayOfMonth = date.getDate();
			
			if (dayOfMonth !== allowedEffectiveDates.payment_day) {
				setDateError(`Only the ${allowedEffectiveDates.payment_day}th of any month is allowed`);
			} else {
				setDateError("");
			}
		} else {
			setDateError("");
		}
	};

	const getHelperText = () => {
		if (dateError) {
			return dateError;
		}
		if (changeReason === "INCREASE" && allowedEffectiveDates?.has_restriction) {
			return allowedEffectiveDates.allowed_range || `Only the ${allowedEffectiveDates.payment_day}th of any month`;
		}
		return "";
	};

	return (
		<>
			<Grid container component="form" noValidate autoComplete="off" spacing={2} sx={{marginTop:"1px"}}>
				<Grid item xs={12} sm={6}>
					<TextField size="small" fullWidth value={unitRent} label="Unit Rent" onChange={handleChangeUnitRent} />
				</Grid>
				<Grid item xs={12} sm={6}>
					<FormControl fullWidth size="small">
						<InputLabel id="demo-simple-select-label">Unit Rent Cycle</InputLabel>
						<Select label="Unit Rent cycle" labelId="rental-type" id="rental-type-id" defaultValue={rentCycle} onChange={handleChangeRentCycle}>
								<MenuItem value="MONTHLY">MONTHLY</MenuItem>
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={6}>
					<FormControl fullWidth size="small">
						<InputLabel id="change-reason-label">Change Reason</InputLabel>
						<Select label="Change Reason" labelId="change-reason-label" id="change-reason-id" value={changeReason} onChange={handleChangeReason}>
								<MenuItem value="MISTAKE">MISTAKE</MenuItem>
								<MenuItem value="INCREASE">INCREASE</MenuItem>
						</Select>
					</FormControl>
				</Grid>
				{changeReason === "INCREASE" && (
					<Grid item xs={12} sm={6}>
						<TextField 
							size="small" 
							fullWidth 
							label="Effective Start Date" 
							type="date" 
							InputLabelProps={{ shrink: true }} 
							value={effectiveStartDate} 
							onChange={validateAndHandleDateChange}
							error={!!dateError}
							helperText={getHelperText()}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<FormControlLabel
						control={
							<Checkbox
								checked={useDeclaredRentForDisplay}
								onChange={handleChangeUseDeclaredRent}
							/>
						}
						label="Use declared rent on receipts"
					/>
				</Grid>
				{useDeclaredRentForDisplay && (
					<Grid item xs={12}>
						<TextField
							size="small"
							fullWidth
							type="number"
							label="Declared unit rent (UGX)"
							value={declaredUnitRent}
							onChange={handleChangeDeclaredUnitRent}
							error={Boolean(declaredRentError)}
							helperText={
								declaredRentError ||
								(declaredRentRatio != null
									? `Actual rent: ${formatNumberString(unitRent)} — Receipts will show ${formatNumberString(declaredUnitRent)} (${declaredRentRatio}%)`
									: "Must be less than actual unit rent.")
							}
						/>
					</Grid>
				)}
				{!useDeclaredRentForDisplay && (
					<Grid item xs={12}>
						<Typography variant="caption" color="text.secondary">
							Receipts show actual rent. Enable declared rent to show a lower amount on tenant receipts only.
						</Typography>
					</Grid>
				)}
			</Grid>
		</>
	);
}
